import { type FC, Fragment } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';

import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import {
    getCreatorsAccordionTabs,
    readCreatorsAccordionTabData,
} from '../helpers/getCreatorsAccordionTabs.ts';
import { PdpRespCreatorsPanelCreator } from './PdpRespCreatorsPanelCreator.tsx';

import { type PdpRespCreatorsPanel_item$data } from './__generated__/PdpRespCreatorsPanel_item.graphql.ts';
import { type PdpRespCreatorsPanel_item$key } from './__generated__/PdpRespCreatorsPanel_item.graphql.ts';

import styles from './PdpRespCreatorsPanel.module.css';

type CreatorTab = NonNullable<
    NonNullable<NonNullable<PdpRespCreatorsPanel_item$data['storyTelling']>['tabs']>[number]
>;

const itemFragment = graphql`
    fragment PdpRespCreatorsPanel_item on Item {
        storyTelling: storyTellingModule(shortTitles: true) {
            tabs {
                ...getCreatorsAccordionTabs_storyTellingTab
                smallImage
                mainImage
                ...PdpRespCreatorsPanelCreator_storyTellingTab
            }
        }
    }
`;

export const PdpRespCreatorsPanel: FC<{
    item: PdpRespCreatorsPanel_item$key;
}> = ({ item: itemRef }) => {
    const item = useFragment(itemFragment, itemRef);
    const { isDesktop } = useServerVarsContext();
    const creatorTabs = getCreatorsAccordionTabs<CreatorTab>(item?.storyTelling?.tabs);
    const hasAnyCreatorImage = creatorTabs.some(tab => !!(tab.smallImage || tab.mainImage));

    if (!creatorTabs.length) {
        return null;
    }

    return (
        <div
            className={classnames(dibsCss.flex, dibsCss.flexCol, dibsCss.gapSmall, dibsCss.wFull)}
            data-tn="pdp-creators-panel"
        >
            {creatorTabs.map((tab, index) => (
                <Fragment key={`${readCreatorsAccordionTabData(tab).tabType}-${index}`}>
                    {index > 0 && (
                        <div
                            className={classnames(
                                dibsCss.flex,
                                dibsCss.wFull,
                                isDesktop && dibsCss.gapLarge
                            )}
                            aria-hidden
                        >
                            <div
                                className={classnames(
                                    dibsCss.h1px,
                                    dibsCss.bgDolphin,
                                    isDesktop ? dibsCss.flex1 : dibsCss.wFull,
                                    isDesktop && dibsCss.minW0
                                )}
                            />
                            {isDesktop && hasAnyCreatorImage && (
                                <div
                                    className={classnames(styles.imageSpacer, dibsCss.flexShrink0)}
                                />
                            )}
                        </div>
                    )}
                    <PdpRespCreatorsPanelCreator
                        storyTellingTab={tab}
                        reserveImageColumn={hasAnyCreatorImage}
                    />
                </Fragment>
            ))}
        </div>
    );
};
